import { API_BASE } from '../../apiBase';
// src/projects/project_finances/index.js
import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ChevronDown, Check, Trash2, Pencil } from 'lucide-react';

import RevenueTab from './RevenueTab';
import ExpenseTab from './ExpenseTab';
import ProfitLossTab from './ProfitLossTab';

// Default visibility for the Project Financial Summary card.
const DEFAULT_SUMMARY = {
  totalInvoiced: true,
  totalQuoted: false,
  totalChangeOrders: false,
  totalPaid: true,
  outstanding: true,
  grossProfit: false,
  profitMargin: false,
  totalExpenses: true,
  netProfit: false,
};

function ProjectFinances({ highlightExpenseId, openExpenses }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [payments, setPayments] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [informalEstimates, setInformalEstimates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [activeTab, setActiveTab] = useState(
    localStorage.getItem('projectFinancesActiveTab') || 'revenue'
  );

  useEffect(() => {
    localStorage.setItem('projectFinancesActiveTab', activeTab);
  }, [activeTab]);

  // Arriving from the Expense Ledger: force the Expenses sub-tab so the
  // highlighted row (or the pending-review queue) is on screen.
  useEffect(() => {
    if (highlightExpenseId || openExpenses) setActiveTab('expenses');
  }, [highlightExpenseId, openExpenses]);

  const [contextMenu, setContextMenu] = useState({ 
  visible: false, 
  x: 0, 
  y: 0, 
  targetId: null, 
  type: null 
});
console.log("Current Project ID:", id);
const handleRightClick = (e, type, id) => {
  e.preventDefault(); // This is the most important part—it stops the browser menu
  console.log("Right click triggered for:", type, id);
  setContextMenu({
    visible: true,
    x: e.clientX,
    y: e.clientY,
    targetId: id,
    type: type
  });
};

// Right-click → Edit: open the relevant create page deep-linked to this record,
// which loads it for editing (and saves back to the SAME record).
const handleEdit = (type, targetId) => {
  setContextMenu(prev => ({ ...prev, visible: false }));
  if (type === 'invoice') navigate(`/billing/create-invoice?id=${targetId}`);
  else if (type === 'quote') navigate(`/billing/create-quote?id=${targetId}`);
};

const handleDelete = async (type, id) => {
  const label = type === 'informalestimate' ? 'estimate log note' : type;
  // 1. Double check with a popup
  if (!window.confirm(`Are you sure you want to delete this ${label}?`)) return;

  try {
    const token = localStorage.getItem('token');

    // 2. Standardize the endpoint (invoice -> invoices, etc.)
    // If your backend uses 'payments', 'quotes', and 'invoices'
    const endpoint = type === 'changeorder' ? 'change-orders'
      : type === 'informalestimate' ? 'informal-estimates'
      : `${type}s`;

    await axios.delete(`${API_BASE}/${endpoint}/${id}`, {
      headers: { Authorization: token }
    });

    // 3. Close the menu and refresh the data
    setContextMenu({ ...contextMenu, visible: false });
    fetchFinances(); 
  } catch (err) {
    console.error(`Failed to delete ${type}:`, err);
    alert(`Could not delete this ${type}.`);
  }
};

// src/projects/project_finances/index.js

// ... existing state and handle functions ...

useEffect(() => {
  // This function closes the menu
  const closeMenu = () => {
    setContextMenu(prev => ({ ...prev, visible: false }));
  };

  // Listen for any click on the window
  window.addEventListener('click', closeMenu);

  // Clean up the listener when the component unmounts
  return () => window.removeEventListener('click', closeMenu);
}, []); // Empty dependency array means it sets up once on load

  // Summary toggles are saved PER PROJECT (localStorage keyed by project id),
  // so each project remembers its own layout instead of sharing one global state.
  const [visibleSummaries, setVisibleSummaries] = useState(DEFAULT_SUMMARY);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API_BASE}/me`, { headers: { Authorization: token } });
        if (cancelled) return;
        const cfg = res.data?.projectSummaryConfigs?.[id];
        setVisibleSummaries(cfg ? { ...DEFAULT_SUMMARY, ...cfg } : DEFAULT_SUMMARY);
      } catch (_) {
        if (!cancelled) setVisibleSummaries(DEFAULT_SUMMARY);
      }
    })();
    return () => { cancelled = true; };
  }, [id]);

  const [showSummaryDropdown, setShowSummaryDropdown] = useState(false);

const handleStatusChange = async (type, id, newStatus) => {
  try {
    const token = localStorage.getItem('token');
    // This assumes your backend has a route like /invoices/:id/status or handles it via PUT
    await axios.put(`${API_BASE}/${type}s/${id}`, { status: newStatus }, {
      headers: { Authorization: token }
    });
    fetchFinances(); // Refresh the data to update totals
  } catch (err) {
    console.error("Failed to update status:", err);
    alert("Could not update status.");
  }
};
  const summaryOptions = [
    { key: 'totalInvoiced', label: 'Total Invoiced' },
    { key: 'totalQuoted', label: 'Total Quoted' },
    { key: 'totalPaid', label: 'Total Paid' },
    { key: 'outstanding', label: 'Outstanding' },
    { key: 'grossProfit', label: 'Gross Profit' },
    { key: 'profitMargin', label: 'Gross Margin' },
    { key: 'totalExpenses', label: 'Total Expenses' },
    { key: 'netProfit', label: 'Cash Position' },
    { key: 'totalValue', label: 'Total Value' }, 
  ];

  const definitions = {
  totalValue: "The true scale of the job: the higher of either your total invoiced or total cash received.",
  totalInvoiced: "The total amount officially requested through invoices (excluding drafts).",
  totalQuoted: "The total value of all quotes/estimates provided for this project.",
  totalChangeOrders: "Additional work or adjustments outside the original contract scope.",
  totalPaid: "The actual cash physically in your possession (Invoices marked 'Paid' + manual entries).",
  outstanding: "Money that has been invoiced but not yet received.",
  grossProfit: "Standard accounting: revenue (Total Value) minus direct job costs (Total Expenses). What the job earns before company overhead.",
  profitMargin: "Gross Profit as a percentage of Total Value. The standard gross margin for the job.",
  totalExpenses: "Total cost of materials, labor, and direct job-related expenses.",
  netProfit: "Cash in hand today: Total Paid minus Total Expenses. Reflects your current liquidity, not final profit."
};

const InfoTooltip = ({ text }) => (
  <div className="group relative inline-block ml-2 cursor-help">
    <div className="w-4 h-4 rounded-full border border-zinc-500 flex items-center justify-center text-[10px] text-zinc-500 group-hover:border-white group-hover:text-white transition-colors">
      i
    </div>
    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:block w-48 p-2 bg-gray-800 text-xs text-zinc-200 rounded shadow-xl border border-gray-700 z-[60]">
      {text}
      <div className="absolute top-full left-1/2 -translate-x-1/2 border-8 border-transparent border-t-gray-800" />
    </div>
  </div>
);

const toggleSummary = (key) => {
  const updatedConfig = { ...visibleSummaries, [key]: !visibleSummaries[key] };
  setVisibleSummaries(updatedConfig);
  // Persist PER PROJECT on the user (syncs across devices).
  try {
    const token = localStorage.getItem('token');
    if (id && token) {
      axios.put(`${API_BASE}/user/summary-config/${id}`, updatedConfig, { headers: { Authorization: token } });
    }
  } catch (err) {
    console.error("Failed to persist summary preferences:", err);
  }
};

 const fetchFinances = async () => {
  try {
    setLoading(true);
    const token = localStorage.getItem('token');

    // ALL SIX IN PARALLEL — one round-trip time instead of six stacked.
    // Expenses are filtered SERVER-SIDE (?project=) so we download one
    // project's worth, not the whole company's history.
    const h = { headers: { Authorization: token } };
    const [projectRes, invoicesRes, quotesRes, paymentsRes, ieRes, expensesRes] = await Promise.all([
      axios.get(`${API_BASE}/projects/${id}`, h),
      axios.get(`${API_BASE}/invoices?project=${id}`, h),
      axios.get(`${API_BASE}/quotes?project=${id}`, h),
      axios.get(`${API_BASE}/payments/${id}`, h),
      axios.get(`${API_BASE}/projects/${id}/informal-estimates`, h).catch(() => ({ data: [] })),
      axios.get(`${API_BASE}/expenses?project=${id}`, h),
    ]);

    setProject(projectRes.data);
    setInvoices(invoicesRes.data.filter(inv => inv.project?._id === id || inv.project === id));
    setQuotes(quotesRes.data.filter(q => q.project?._id === id || q.project === id));
    setPayments(paymentsRes.data || []);
    setInformalEstimates(ieRes.data || []);
    // Safety net: filter client-side too, in case an old backend ignores ?project=
    setExpenses((expensesRes.data || []).filter(exp => {
      const expProjId = exp.project?._id || exp.project;
      return String(expProjId) === String(id);
    }));

  } catch (err) {
    console.error('Failed to load finances:', err);
    setError('Could not load financial data.');
  } finally {
    setLoading(false);
  }
};

  useEffect(() => {
    fetchFinances();
  }, [id]);

// Calculations
  const totalInvoiced = useMemo(() => 
    invoices.filter(inv => inv.status !== 'Draft' && inv.status !== 'Revised').reduce((sum, inv) => sum + (inv.total || 0), 0), // Revised = superseded by an R-revision — never counts
  [invoices]);

  const totalQuoted = useMemo(() => 
    quotes.reduce((sum, q) => sum + (q.total || 0), 0), 
  [quotes]);

  const totalChangeOrders = 0;

  const totalPaid = useMemo(() => {
    const paidInvoices = invoices.filter(inv => inv.status === 'Paid');
    const fromInvoices = paidInvoices.reduce((sum, inv) => sum + (inv.total || 0), 0);
    // Skip payments tied to an invoice already counted as Paid — a receipt
    // records a payment AND marks its invoice Paid, which double-counted the
    // same money ($4,500 invoice + $4,500 receipt = a phantom $9,000).
    const paidNumbers = new Set(paidInvoices.map(inv => inv.number));
    const manual = payments.reduce((sum, p) => {
      if (p.invoice && p.invoice !== '—' && paidNumbers.has(p.invoice)) return sum;
      return sum + (p.amount || 0);
    }, 0);
    return fromInvoices + manual;
  }, [invoices, payments]);

  const outstanding = useMemo(() => Math.max(0, totalInvoiced - totalPaid), [totalInvoiced, totalPaid]);

const totalExpenses = useMemo(() => {
    return expenses.reduce((sum, exp) => {
      // Skip 'Secondary' linked slips (the merchant/vendor pair we don't double-count).
      if (exp.linkId && exp.isPrimary === false) {
        return sum;
      }
      // Use the grand total, and DO NOT Math.abs — returns are stored negative so
      // they subtract naturally (a $50 return reduces expenses by $50).
      return sum + Number(exp.totalAmount != null ? exp.totalAmount : (exp.amount || 0));
    }, 0);
  }, [expenses]);

  // The 'Truth' of the project's size: Use whichever is higher (Paper vs. Cash)
  const totalValue = Math.max(totalInvoiced, totalPaid);

  // LEGITIMATE accounting definitions:
  // Gross Profit = revenue − direct job costs (COGS). Uses Total Value as revenue.
  // Cash Position (formerly mislabeled "gross/net profit") = cash collected − cash spent.
  const grossProfit = totalValue - totalExpenses;
  const cashPosition = totalPaid - totalExpenses;
  const profitMargin = totalValue > 0 ? ((grossProfit / totalValue) * 100).toFixed(1) + '%' : '0%';

  // Always show exact cents. Round to the cent first (kills floating-point drift
  // like 1234.9999998 that would otherwise display as a rounded-up 1,235).
  const fmtMoney = (n) => (Math.round((Number(n) || 0) * 100) / 100)
    .toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  return (
    <div className="min-h-screen bg-gray-950 text-zinc-100 p-8 max-md:p-4 space-y-10 max-md:space-y-5">

      {/* Top Financial Summary */}
      <div className="bg-gray-900 rounded-2xl p-8 max-md:p-4 border border-gray-800/50">
        <div className="flex justify-between items-center mb-6 max-md:flex-col max-md:items-stretch max-md:gap-3 max-md:mb-4">
          <h2 className="text-xl font-semibold">Project Financial Summary</h2>
          
          <div className="relative">
            <button
              onClick={() => setShowSummaryDropdown(!showSummaryDropdown)}
              className="flex items-center gap-2 px-5 py-2.5 max-md:py-3 max-md:justify-center bg-gray-800 hover:bg-gray-700 rounded-xl text-sm border border-gray-700 max-md:w-full"
            >
              Customize Summary
              <ChevronDown size={16} />
            </button>

            {showSummaryDropdown && (
              <div className="absolute right-0 mt-2 w-[560px] max-md:w-full max-md:left-0 max-md:right-0 bg-gray-900 border border-gray-700 rounded-xl shadow-xl z-50 p-4 grid grid-cols-3 max-md:grid-cols-1 gap-2">
                {summaryOptions.map(item => (
                  <div
                    key={item.key}
                    onClick={() => toggleSummary(item.key)}
                    className="flex items-center gap-3 px-4 py-3 hover:bg-gray-800 rounded-lg cursor-pointer"
                  >
                    <div className={`w-5 h-5 border rounded flex items-center justify-center ${visibleSummaries[item.key] ? 'bg-indigo-600 border-indigo-600' : 'border-gray-600'}`}>
                      {visibleSummaries[item.key] && <Check size={14} className="text-white" />}
                    </div>
                    <span className="text-sm">{item.label}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-6 max-md:gap-2.5">
          {visibleSummaries.totalValue && (
            <div className="bg-gray-950 rounded-2xl max-md:rounded-xl p-6 max-md:p-3 border border-gray-800/50">
              <div className="flex items-center">
                <p className="text-zinc-400 text-sm max-md:text-[11px]">Total Value</p>
                <InfoTooltip text="The true scale of the job: the higher of either your total invoiced or total cash received." />
              </div>
              <p className="text-4xl max-md:text-base font-bold text-blue-400 mt-3 max-md:mt-1 max-md:break-words">${fmtMoney(totalValue)}</p>
            </div>
          )}

          {visibleSummaries.totalInvoiced && (
            <div className="bg-gray-950 rounded-2xl max-md:rounded-xl p-6 max-md:p-3 border border-gray-800/50">
              <div className="flex items-center">
                <p className="text-zinc-400 text-sm max-md:text-[11px]">Total Invoiced</p>
                <InfoTooltip text="Total amount officially requested through invoices (excluding drafts)." />
              </div>
              <p className="text-4xl max-md:text-base font-bold text-indigo-400 mt-3 max-md:mt-1 max-md:break-words">${fmtMoney(totalInvoiced)}</p>
            </div>
          )}

          {visibleSummaries.totalQuoted && (
            <div className="bg-gray-950 rounded-2xl max-md:rounded-xl p-6 max-md:p-3 border border-gray-800/50">
              <div className="flex items-center">
                <p className="text-zinc-400 text-sm max-md:text-[11px]">Total Quoted</p>
                <InfoTooltip text="Total value of all quotes and estimates provided for this project." />
              </div>
              <p className="text-4xl max-md:text-base font-bold text-purple-400 mt-3 max-md:mt-1 max-md:break-words">${fmtMoney(totalQuoted)}</p>
            </div>
          )}

          {visibleSummaries.totalPaid && (
            <div className="bg-gray-950 rounded-2xl max-md:rounded-xl p-6 max-md:p-3 border border-gray-800/50">
              <div className="flex items-center">
                <p className="text-zinc-400 text-sm max-md:text-[11px]">Total Paid</p>
                <InfoTooltip text="Actual cash physically received (Paid invoices + manual payment entries)." />
              </div>
              <p className="text-4xl max-md:text-base font-bold text-green-400 mt-3 max-md:mt-1 max-md:break-words">${fmtMoney(totalPaid)}</p>
            </div>
          )}

          {visibleSummaries.outstanding && (
            <div className="bg-gray-950 rounded-2xl max-md:rounded-xl p-6 max-md:p-3 border border-gray-800/50">
              <div className="flex items-center">
                <p className="text-zinc-400 text-sm max-md:text-[11px]">Outstanding</p>
                <InfoTooltip text="Money that has been invoiced but not yet received." />
              </div>
              <p className="text-4xl max-md:text-base font-bold text-orange-400 mt-3 max-md:mt-1 max-md:break-words">${fmtMoney(outstanding)}</p>
            </div>
          )}

          {visibleSummaries.profitMargin && (
            <div className="bg-gray-950 rounded-2xl max-md:rounded-xl p-6 max-md:p-3 border border-gray-800/50">
              <div className="flex items-center">
                <p className="text-zinc-400 text-sm max-md:text-[11px]">Gross Margin</p>
                <InfoTooltip text="Gross Profit as a percentage of Total Value. The standard gross margin for the job." />
              </div>
              <p className="text-4xl max-md:text-base font-bold text-emerald-400 mt-3 max-md:mt-1 max-md:break-words">{profitMargin}</p>
            </div>
          )}

          {visibleSummaries.grossProfit && (
            <div className="bg-gray-950 rounded-2xl max-md:rounded-xl p-6 max-md:p-3 border border-gray-800/50">
              <div className="flex items-center">
                <p className="text-zinc-400 text-sm max-md:text-[11px]">Gross Profit</p>
                <InfoTooltip text="Revenue (Total Value) minus direct job costs (Total Expenses). What the job earns before company overhead." />
              </div>
              <p className="text-4xl max-md:text-base font-bold text-emerald-400 mt-3 max-md:mt-1 max-md:break-words">${fmtMoney(grossProfit)}</p>
            </div>
          )}

          {visibleSummaries.totalExpenses && (
            <div className="bg-gray-950 rounded-2xl max-md:rounded-xl p-6 max-md:p-3 border border-gray-800/50">
              <div className="flex items-center">
                <p className="text-zinc-400 text-sm max-md:text-[11px]">Total Expenses</p>
                <InfoTooltip text="Total cost of materials, labor, and direct job-related expenses." />
              </div>
              <p className="text-4xl max-md:text-base font-bold text-red-400 mt-3 max-md:mt-1 max-md:break-words">${fmtMoney(totalExpenses)}</p>
            </div>
          )}

          {visibleSummaries.netProfit && (
            <div className="bg-gray-950 rounded-2xl max-md:rounded-xl p-6 max-md:p-3 border border-gray-800/50">
              <div className="flex items-center">
                <p className="text-zinc-400 text-sm max-md:text-[11px]">Cash Position</p>
                <InfoTooltip text="Cash in hand today: Total Paid minus Total Expenses. Liquidity, not final profit." />
              </div>
              <p className="text-4xl max-md:text-base font-bold text-emerald-400 mt-3 max-md:mt-1 max-md:break-words">${fmtMoney(cashPosition)}</p>
            </div>
          )}
        </div> {/* This closes the Grid */}
      </div> {/* This closes the Summary Section */}

      {/* Tabs */}
      <div className="bg-gray-900 rounded-2xl border border-gray-800/50 overflow-hidden">
        <div className="border-b border-gray-800 px-8 max-md:px-4">
          <div className="flex gap-10 max-md:gap-6 max-md:overflow-x-auto">
            <button 
              onClick={() => setActiveTab('revenue')} 
              className={`py-5 max-md:py-3 font-medium border-b-2 transition-colors text-lg max-md:text-sm whitespace-nowrap ${activeTab ==='revenue' ? 'border-indigo-500 text-white' : 'border-transparent text-zinc-400 hover:text-white'}`}
            >
              Revenue
            </button>
            <button 
              onClick={() => setActiveTab('expenses')} 
              className={`py-5 max-md:py-3 font-medium border-b-2 transition-colors text-lg max-md:text-sm whitespace-nowrap ${activeTab ==='expenses' ? 'border-indigo-500 text-white' : 'border-transparent text-zinc-400 hover:text-white'}`}
            >
              Expenses
            </button>
            <button 
              onClick={() => setActiveTab('profitloss')} 
              className={`py-5 max-md:py-3 font-medium border-b-2 transition-colors text-lg max-md:text-sm whitespace-nowrap ${activeTab ==='profitloss' ? 'border-indigo-500 text-white' : 'border-transparent text-zinc-400 hover:text-white'}`}
            >
              Profit & Loss
            </button>
          </div>
        </div>

        <div className="p-8 max-md:p-3">
          {activeTab === 'revenue' && (
  <RevenueTab
    invoices={invoices}
    quotes={quotes}
    payments={payments}
    informalEstimates={informalEstimates}
    setInformalEstimates={setInformalEstimates}
    onRefresh={fetchFinances}
    handleStatusChange={handleStatusChange} 
    handleRightClick={handleRightClick}
    projectId={id}
  />
)}
            {activeTab === 'expenses' && (
               <ExpenseTab
    projectId={id}
    expenses={expenses}
    setExpenses={setExpenses}
    onRefresh={fetchFinances}
    handleRightClick={handleRightClick}
    highlightExpenseId={highlightExpenseId}
  />
            )}
          {activeTab === 'profitloss' && <ProfitLossTab 
            totalInvoiced={totalInvoiced}
            totalExpenses={totalExpenses}
            totalPaid={totalPaid}
            grossProfit={grossProfit}
            cashProfit={cashPosition}
            profitMargin={profitMargin}
          />}
        </div>
      </div>
      {contextMenu.visible && (
          <div 
            className="fixed bg-gray-900 border border-gray-700 py-2 rounded-lg shadow-2xl z-[9999] w-48"
            style={{ 
              top: `${contextMenu.y}px`, 
              left: `${contextMenu.x}px` 
            }}
          >
            {(contextMenu.type === 'invoice' || contextMenu.type === 'quote') && (
              <button
                onClick={() => handleEdit(contextMenu.type, contextMenu.targetId)}
                className="w-full text-left px-4 py-2 text-indigo-400 hover:bg-indigo-500/10 flex items-center gap-2 transition-colors font-medium border-none bg-transparent cursor-pointer"
              >
                <Pencil size={16} />
                Edit {contextMenu.type.charAt(0).toUpperCase() + contextMenu.type.slice(1)}
              </button>
            )}
            <button
              onClick={() => handleDelete(contextMenu.type, contextMenu.targetId)}
              className="w-full text-left px-4 py-2 text-red-500 hover:bg-red-500/10 flex items-center gap-2 transition-colors font-medium border-none bg-transparent cursor-pointer"
            >
              <Trash2 size={16} />
              {contextMenu.type === 'informalestimate'
                ? 'Delete'
                : `Delete ${contextMenu.type.charAt(0).toUpperCase() + contextMenu.type.slice(1)}`}
            </button>
          </div>
        )}
    </div>
  );
}

export default ProjectFinances;